import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ConnectionStatusChip, MachineStatusChip } from '../../Components/Vending/MachineStatusChip';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';

interface MachineRow {
  id: number;
  provider_machine_id: number;
  machine_name: string | null;
  city: string | null;
  state: string | null;
  connection_status: string;
  status: string;
  location_map_status: string;
  last_communication: string | null;
  last_inventory_sync: string | null;
}

interface CustomerDetailResponse {
  data: {
    cid: number;
    customer_name: string;
    machines: MachineRow[];
  };
  meta: { as_of: string };
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const headCellSx = {
  py: 1.25,
  px: 2,
  fontWeight: 700,
  fontSize: '0.6875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: COLORS.lightGray,
  backgroundColor: '#f8f9fa',
  borderBottom: `1px solid ${COLORS.lightGray}35`,
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  py: 1.75,
  px: 2,
  borderBottom: `1px solid ${COLORS.lightGray}30`,
  verticalAlign: 'middle' as const,
};

const mapChip = (status: string) => {
  const mapped = status === 'mapped';
  return (
    <Chip
      size="small"
      label={mapped ? 'Mapped' : status || 'Unmapped'}
      sx={{
        height: 22,
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'capitalize',
        bgcolor: mapped ? `${COLORS.success}18` : `${COLORS.orange}18`,
        color: mapped ? COLORS.success : COLORS.orange,
      }}
    />
  );
};

export const VendingCustomerDetailPage = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<CustomerDetailResponse['data'] | null>(null);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient(`/customers/${cid}`);
      const json: CustomerDetailResponse = await response.json();
      setData(json.data);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customer');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    load();
  }, [load]);

  const machines = data?.machines ?? [];

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return machines;
    return machines.filter((m) => {
      const hay = [
        m.machine_name,
        `VM-${m.provider_machine_id}`,
        m.city,
        m.state,
        m.connection_status,
        m.status,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [machines, searchInput]);

  const summary = useMemo(() => {
    const online = machines.filter((m) => m.connection_status === 'online').length;
    const offline = machines.filter((m) => m.connection_status === 'offline').length;
    const mapped = machines.filter((m) => m.location_map_status === 'mapped').length;
    return { online, offline, mapped, total: machines.length };
  }, [machines]);

  const subtitle = useMemo(() => {
    if (!data) return undefined;
    const parts = [
      `CID ${data.cid}`,
      `${summary.total} machine${summary.total === 1 ? '' : 's'}`,
      `${summary.online} online`,
    ];
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [data, summary, asOf]);

  return (
    <PageContainer>
      <Header
        title={data?.customer_name || `Customer ${cid}`}
        subtitle={subtitle}
        count={filtered.length}
        onBack={() => navigate('/vending/customers')}
      >
        <FreshnessBadge asOf={asOf} />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!loading && data && (
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: { xs: 2, md: 3 },
              pb: 2,
              flexShrink: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            {[
              { label: 'Machines', value: summary.total, color: COLORS.teal },
              { label: 'Online', value: summary.online, color: COLORS.success },
              { label: 'Offline', value: summary.offline, color: summary.offline ? COLORS.red : COLORS.lightGray },
              { label: 'Mapped', value: summary.mapped, color: COLORS.darkGray },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  px: 1.5,
                  py: 1,
                  minWidth: 96,
                  borderRadius: 1,
                  border: `1px solid ${COLORS.lightGray}40`,
                  borderTop: `3px solid ${item.color}`,
                  backgroundColor: COLORS.white,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  {item.label}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: item.color, lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}

            <TextField
              size="small"
              label="Search machines"
              placeholder="Name, city, status…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ ml: { md: 'auto' }, minWidth: 220, flex: { xs: '1 1 100%', md: '0 1 240px' } }}
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 } }}>
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && data && filtered.length === 0 && (
          <Box
            sx={{
              mx: { xs: 2, md: 3 },
              textAlign: 'center',
              py: 8,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No machines
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              {machines.length === 0
                ? 'This customer has no mapped vending machines yet.'
                : 'No machines match the current search.'}
            </Typography>
          </Box>
        )}

        {!loading && !error && data && filtered.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${COLORS.lightGray}40`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Machine</TableCell>
                      <TableCell sx={headCellSx}>Location</TableCell>
                      <TableCell sx={headCellSx}>Connection</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Map</TableCell>
                      <TableCell sx={headCellSx}>Last communication</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((m) => (
                      <TableRow
                        key={m.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#fafbfc' },
                        }}
                        onClick={() => navigate(`/vending/machines/${m.id}`)}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.teal }}>
                            {m.machine_name || `VM-${m.provider_machine_id}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                            ID {m.provider_machine_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="body2">
                            {[m.city, m.state].filter(Boolean).join(', ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <ConnectionStatusChip status={m.connection_status} />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <MachineStatusChip status={m.status} />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {mapChip(m.location_map_status)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(m.last_communication)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
